'use client';

import { c as _c } from "react/compiler-runtime";
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { unique } from '@lokalise/universal-ts-utils/array/unique';
import type { Data } from '@repo/strapi-types';
import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { Suspense, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Pagination } from '@/app/[locale]/_components/Pagination';
import { MarketoForm } from '@/app/[locale]/blog/_components/MarketoForm';
import AppLink from '@/components/elementary/AppLink';
import { Tag } from '@/components/elementary/Tag';
import { AppField } from '@/components/forms/AppField';
import { AppForm } from '@/components/forms/AppForm';
import { Close } from '@/components/icons/Close';
import { Search } from '@/components/icons/Search';
import { ChangelogItems } from '@/components/page-builder/global/changelog/ChangelogItem';
import Heading from '@/components/typography/Heading';
import { Paragraph } from '@/components/typography/Paragraph';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import useChangelogIndex from '@/hooks/useChangelogIndex';
import { usePathname } from '@/i18n/navigation';
import { CHANGELOG_ENTRIES_PER_PAGE, getAllCategories, MARKETO_FORM_IDS } from '@/lib/constants';
import { formatDate } from '@/lib/dates';
import { cn } from '@/lib/styles';
import { CHANGELOG_TEST_IDS } from '@/lib/test-ids';
import type { AppLocale } from '@/types/general';
import { calculateTotalPages } from '@/utils/calculateTotalPages';
const MONTH_FORMAT = 'yyyy-MM';
type ChangelogFilterValues = {
  readonly category?: string | null;
  readonly date?: string | null;
  readonly search?: string | null;
  readonly page?: number;
};
type ChangelogFiltersProps = {
  readonly categories: Data.ContentType<'api::changelog-category.changelog-category'>[];
  readonly changeDates: Data.ContentType<'api::change-log.change-log'>['changeDate'][];
  readonly sidebarInfoBanner?: Data.Component<'global.sidebar-info-banner'> | null;
  readonly filters?: ChangelogFilterValues;
  // Current page of entries, rendered on the server and shown while no filter is set
  readonly children: ReactNode;
};

// Reads the filters from the URL, so it must be wrapped in <Suspense>. Use <ChangelogFilters /> as the fallback to pre-render the unfiltered page.
export const ChangelogFiltersWithSearchParams = props => {
  const $ = _c(16);
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get("category");
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== searchParams) {
    t1 = searchParams.get("date");
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== searchParams) {
    t2 = searchParams.get("search");
    $[4] = searchParams;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== searchParams) {
    t3 = searchParams.get("page") ?? 1;
    $[6] = searchParams;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = Number(t3);
  let t5;
  if ($[8] !== t0 || $[9] !== t1 || $[10] !== t2 || $[11] !== t4) {
    t5 = {
      category: t0,
      date: t1,
      search: t2,
      page: t4
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== props || $[14] !== t5) {
    t6 = <ChangelogFilters {...props} filters={t5} />;
    $[13] = props;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};
export const ChangelogFilters = ({
  categories,
  changeDates,
  sidebarInfoBanner,
  filters = {},
  children
}: ChangelogFiltersProps) => {
  const t = useTranslations('ChangelogFilters');
  const form = useForm<z.infer<FormSchemaType>>({
    resolver: standardSchemaResolver(FilterFormSchema),
    mode: 'onBlur',
    reValidateMode: 'onSubmit',
    defaultValues: {
      search: filters.search ?? ''
    }
  });
  const searchTerm = form.watch('search');
  const selectedCategory = filters.category;
  const selectedDate = filters.date;
  const filteredDates = unique(changeDates.filter(Boolean).map(changeDate => formatDate(changeDate as string, MONTH_FORMAT)));

  // Typing does not add history entries, but keeps the search in the URL like the other filters
  useEffect(() => form.subscribe({
    formState: {
      values: true
    },
    callback: ({
      values
    }) => updateFilters({
      search: values.search
    }, 'replaceState')
  }), [form]);
  const allCategories = getAllCategories(t('allCategories'));
  return <div className="flex lg:flex-row flex-col-reverse lg:gap-17" data-test-id={CHANGELOG_TEST_IDS.FILTERS}>
      <div className="flex flex-1 flex-col">
        {selectedCategory || selectedDate || filters.search ? <Suspense fallback={<FilteredChangelogItemsSkeleton />}>
            <FilteredChangelogItems filters={filters} />
          </Suspense> : children}
      </div>
      <div className="w-full lg:w-71.5 flex flex-col gap-6 lg:pt-14">
        <AppForm form={form} id={filterFormName} onSubmit={() => {}}>
          <AppField containerClassName="lg:flex hidden w-full" fieldClassName="w-full" name="search" placeholder={t('searchPlaceholder')} iconLeft={<Search />} data-test-id={CHANGELOG_TEST_IDS.SEARCH_INPUT} />
          {searchTerm && <GhostButton name="category" onClick={() => form.setValue('search', '')} className="absolute right-3 -translate-y-1/2 top-1/2" data-test-id={CHANGELOG_TEST_IDS.SEARCH_CLEAR}>
              <Close className="text-neutral-light-600 size-4" />
            </GhostButton>}
        </AppForm>

        {sidebarInfoBanner && <div className="@container bg-peach-200 w-full rounded-xl p-5">
            <Paragraph fontSize="headingSmall" className="mb-3 @sidebar-compact:mb-2">
              {sidebarInfoBanner.title}
            </Paragraph>
            <Paragraph fontSize="bodyRegularMedium" className="mb-4 @sidebar-compact:mb-5">
              {sidebarInfoBanner.description}
            </Paragraph>
            <AppLink link={sidebarInfoBanner.link} title={sidebarInfoBanner.link?.label ?? ''} variant="primary" size="medium" className="w-full">
              {sidebarInfoBanner.link?.label}
            </AppLink>
          </div>}

        <div className="h-px w-full bg-neutral-light-500" />
        <div className="flex flex-wrap gap-2.5 justify-center lg:justify-start">
          <GhostButton key={allCategories.id} name="category" onClick={() => updateFilters({
          category: null
        })} data-test-id={CHANGELOG_TEST_IDS.CATEGORY_ALL}>
            <Tag name={allCategories?.name} active={!selectedCategory} customColors={{
            backgroundColor: allCategories.backgroundColor?.color,
            borderColor: allCategories.borderColor?.color
          }} />
          </GhostButton>
          {categories?.map(category => <GhostButton key={category.id} name="category" onClick={() => updateFilters({
          category: category.documentId
        })} data-test-id={CHANGELOG_TEST_IDS.CATEGORY}>
              <Tag name={category?.name} active={selectedCategory === category.documentId} customColors={{
            backgroundColor: category.backgroundColor?.color,
            borderColor: category.borderColor?.color
          }} />
            </GhostButton>)}
        </div>
        <div className="h-px w-full bg-neutral-light-500 lg:flex hidden" />
        <ul className="flex-col gap-2 lg:flex hidden">
          <GhostButton name="date" className="flex justify-start" onClick={() => updateFilters({
          date: null
        })} data-test-id={CHANGELOG_TEST_IDS.FILTERS_ALL}>
            <li>
              <Paragraph fontSize="bodyRegularLarge" className={cn({
              'font-bold': !selectedDate
            })}>
                {t('allDates')}
              </Paragraph>
            </li>
          </GhostButton>
          {filteredDates.map(date => <GhostButton key={date} name="date" onClick={() => updateFilters({
          date
        })} data-test-id={CHANGELOG_TEST_IDS.FILTERS_DATE}>
              <li>
                <Paragraph fontSize="bodyRegularLarge" className={cn({
              'font-bold': selectedDate === date
            })}>
                  {formatDate(date, 'MMMM yyyy')}
                </Paragraph>
              </li>
            </GhostButton>)}
        </ul>
        <div className="h-px w-full bg-neutral-light-500" />
        <div className="flex flex-col gap-3 w-full lg:max-w-71.5 bg-peach-100 p-5 rounded-xl lg:sticky lg:top-33">
          <Heading tag="h2" fontSize="headingSmall">
            {t('subscriptionTitle')}
          </Heading>
          <Paragraph fontSize="bodyRegularMedium">{t('subscriptionDescription')}</Paragraph>
          <MarketoForm id={MARKETO_FORM_IDS.NEWSLETTER_SUBSCRIPTION_LOKALOG} loadingSkeleton={<NewsletterSubscriptionSkeleton />} className="overflow-hidden" />
        </div>
      </div>
    </div>;
};
const FilterFormSchema = z.object({
  search: z.string().optional()
});
type FormSchemaType = typeof FilterFormSchema;
export const filterFormName = 'changelogFilter';

/**
 * Filters are applied on the client, so the URL is updated with the native History API, without a server round trip.
 * Any filter change goes back to the first page of results.
 */
const updateFilters = (changes: Pick<ChangelogFilterValues, 'category' | 'date' | 'search'>, method: 'pushState' | 'replaceState' = 'pushState') => {
  const params = new URLSearchParams(window.location.search);
  params.delete('page');
  for (const [key, value] of Object.entries(changes)) {
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
  }
  const query = params.toString();
  window.history[method](null, '', query ? `?${query}` : window.location.pathname);
};
const filterChangelogEntries = (entries: Data.ContentType<'api::change-log.change-log'>[], {
  category,
  date,
  search
}: ChangelogFilterValues) => entries.filter(entry => (!category || entry.categories?.some(entryCategory => entryCategory?.documentId === category)) && (!date || formatDate(entry.changeDate as string, MONTH_FORMAT) === date) && (!search || entry.htmlContent?.toLowerCase().includes(search.toLowerCase())));

// Loads all entries only once a filter is set, then paginates the results on the client
const FilteredChangelogItems = t0 => {
  const $ = _c(12);
  const {
    filters
  } = t0;
  const locale = useLocale() as AppLocale;
  const pathname = usePathname();
  let t1;
  if ($[0] !== locale) {
    t1 = {
      locale
    };
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const entries = filterChangelogEntries(useChangelogIndex(t1), filters);
  const pageCount = calculateTotalPages(entries.length, CHANGELOG_ENTRIES_PER_PAGE);
  const requestedPage = filters.page ?? 1;
  const page = Number.isInteger(requestedPage) && requestedPage >= 1 && requestedPage <= pageCount ? requestedPage : 1;
  const T0 = ChangelogItems;
  const t2 = entries.slice((page - 1) * CHANGELOG_ENTRIES_PER_PAGE, page * CHANGELOG_ENTRIES_PER_PAGE);
  let t3;
  if ($[2] !== T0 || $[3] !== t2) {
    t3 = <T0 entries={t2} />;
    $[2] = T0;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== page || $[6] !== pageCount || $[7] !== pathname) {
    t4 = <Pagination totalPages={pageCount} currentPage={page} useSearchParamPagination={true} pathname={pathname} />;
    $[5] = page;
    $[6] = pageCount;
    $[7] = pathname;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
const FilteredChangelogItemsSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col gap-3 lg:pt-14"><Skeleton className="h-6 w-40" /><Skeleton className="h-10 w-full" /><Skeleton className="h-40 w-full" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const GhostButton = t0 => {
  const $ = _c(9);
  const {
    children,
    name,
    onClick,
    className
  } = t0;
  let t1;
  if ($[0] !== onClick) {
    t1 = () => {
      onClick();
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    };
    $[0] = onClick;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleClick = t1;
  let t2;
  if ($[2] !== className) {
    t2 = cn("flex justify-start", className);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== handleClick || $[6] !== name || $[7] !== t2) {
    t3 = <Button name={name} variant="none" size="none" className={t2} onClick={handleClick}>{children}</Button>;
    $[4] = children;
    $[5] = handleClick;
    $[6] = name;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const NewsletterSubscriptionSkeleton = () => {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="h-11.25 w-full mb-7.75" />;
    t1 = <Skeleton className="h-9 sm:h-5.75 lg:h-9 w-full mb-5.25 lg:mb-4" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="pt-5">{t0}{t1}<div className="flex flex-col gap-1.5 lg:gap-1.5 mb-3.75 lg:mb-2.5"><Skeleton className="h-2.75 lg:h-4 w-full" /><Skeleton className="h-2.75 lg:h-4 w-1/2" /><Skeleton className="h-2.75 lg:h-4 w-1/3" /></div><Skeleton className="h-11.25 w-full rounded-full" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};